import { isDefined } from '@/utils/isDefined';

export interface CouponResponse {
  errors?: { message: string }[];
  warnings?: { message: string }[];
}

export interface QuizAnswer {
  answerText: string;
  description?: string;
  id: string;
  label?: string;
  selector?: string;
  valueProp?: string;
}

export type ImpactfulQuizAnswer = Required<QuizAnswer>;

export interface QuizQuestion {
  answers: QuizAnswer[];
  columns: 1 | 2;
  id: number;
  maxSelections?: number;
  minSelections?: number;
  title: string;
}

export interface ImpactfulQuizQuestion extends QuizQuestion {
  answers: ImpactfulQuizAnswer[];
}

export interface QuizResults {
  couponResponse?: CouponResponse;
  existingContact?: boolean;
  selections: [ImpactfulQuizQuestion, ImpactfulQuizQuestion];
}

export function isImpactfulQuizQuestion(question: QuizQuestion): question is ImpactfulQuizQuestion {
  return question.answers.every((answer) => isDefined(answer.selector));
}

export const questions: [QuizQuestion, ImpactfulQuizQuestion, ImpactfulQuizQuestion, QuizQuestion] =
  [
    {
      answers: [
        {
          answerText: 'Just me',
          id: 'ftb25_v3_q1_just-me',
        },
        {
          answerText: 'Me and my partner',
          id: 'ftb25_v3_q1_me-and-partner',
        },
        {
          answerText: 'My family (including kids)',
          id: 'ftb25_v3_q1_my-family',
        },
        {
          answerText: "A friend (I'm gifting)",
          id: 'ftb25_v3_q1_a-friend',
        },
        {
          answerText: 'My office coworkers',
          id: 'ftb25_v3_q1_office-coworkers',
        },
        {
          answerText: 'Another type of business',
          id: 'ftb25_v3_q1_another-type-of-business',
        },
      ],
      columns: 1,
      id: 0,
      maxSelections: 1,
      title: 'Who are you shopping for today?',
    },
    {
      answers: [
        {
          answerText: 'I want healthier snack options',
          description:
            'Fuel your day with better-for-you snacks that are as delicious as they are nutritious, giving you the energy you need without the guilt.',
          id: 'ftb25_v3_q2_healthier-options',
          label: 'Healthy Lifestyle',
          selector: '[RECOM] Quiz: Healthy Snacks',
          valueProp:
            'Fuel your day with better-for-you snacks that are as delicious as they are nutritious, giving you the energy you need without the guilt.',
        },
        {
          answerText: "I'm stocking up for work or school",
          description:
            'Stay energized with snacks that keep you powered through meetings, classes, or long study sessions, all while satisfying your cravings.',
          id: 'ftb25_v3_q2_work-or-school',
          label: 'Work & School',
          selector: '[RECOM] Quiz: Work or School',
          valueProp:
            'Stay energized with snacks that keep you powered through meetings, classes, or long study sessions, all while satisfying your cravings.',
        },
        {
          answerText: "I'm planning a party or event",
          description:
            'Host with ease by serving crowd-pleasing snacks that will impress your guests and make any gathering a hit.',
          id: 'ftb25_v3_q2_party-or-event',
          label: 'Party Planning',
          selector: '[RECOM] Quiz: Planning Party',
          valueProp:
            'Host with ease by serving crowd-pleasing snacks that will impress your guests and make any gathering a hit.',
        },
        {
          answerText: "I'm bored of my usual snacks and am here to explore",
          description:
            "Break out of your snack routine with exciting new flavors and textures that'll surprise and delight with every bite.",
          id: 'ftb25_v3_q2_bored',
          label: 'Unique Snacks',
          selector: '[RECOM] Quiz: Unique Snacks',
          valueProp:
            "Break out of your snack routine with exciting new flavors and textures that'll surprise and delight with every bite.",
        },
        {
          answerText: "I'm looking for something indulgent",
          description:
            'Treat yourself to snacks that satisfy your sweet tooth, with rich and decadent options that feel like a little luxury.',
          id: 'ftb25_v3_q2_something-indulgent',
          label: 'Indulgent Snacks',
          selector: '[RECOM] Quiz: Indulgent',
          valueProp:
            'Treat yourself to snacks that satisfy your sweet tooth, with rich and decadent options that feel like a little luxury.',
        },
        {
          answerText: "I'm looking for a gift",
          description:
            "Send a thoughtful gift with snacks that everyone will enjoy – curated assortments that make any occasion special. Break out of your snack routine with exciting new flavors and textures that'll surprise and delight with every bite.",
          id: 'ftb25_v3_q2_gift',
          label: 'Gifts',
          selector: '[RECOM] Quiz: Gifts',
          valueProp:
            'Send a thoughtful gift with snacks that everyone will enjoy – curated assortments that make any occasion special.',
        },
      ],
      columns: 1,
      id: 1,
      maxSelections: 3,
      title: 'Why are you shopping for snacks today?',
    },
    {
      answers: [
        {
          answerText: 'Sweet and Crunchy',
          description:
            "You're all about that satisfying crunch and touch of sweetness — we've handpicked trail mixes, flavored nuts, popcorn, and sweet treats to keep your snack stash exciting.",
          id: 'ftb25_v3_q3_sweet-crunchy',
          label: 'Sweet & Crunchy',
          selector: '[RECOM] Quiz: Sweet and Crunchy',
          valueProp:
            'Enjoy flavored nuts, trail mix, and chocolatey bites, all made with our carefully sourced ingredients and crafted for that perfect crunch.',
        },
        {
          answerText: 'Savory and Salty',
          description:
            "For those moments when only something salty will do, we've pulled together protein-packed jerky and craveable pretzels to keep your taste buds happy.",
          id: 'ftb25_v3_q3_savory-salty',
          label: 'Savory & Salty',
          selector: '[RECOM] Quiz: Savory and Salty',
          valueProp:
            'Snack on jerky, pretzels, and salted nuts made with high-quality ingredients and the savory crunch you can count on from Nuts.com.',
        },
        {
          answerText: 'Bold and Peppery',
          description:
            'You like your flavors with attitude. These picks — from spicy jerky to peppery trail mixes and zesty flavored nuts — bring the heat and the crunch.',
          id: 'ftb25_v3_q3_bold-peppery',
          label: 'Bold & Peppery',
          selector: '[RECOM] Quiz: Bold and Peppery',
          valueProp:
            'Turn up the flavor with bold jerky, peppery pretzels, and trail mixes that bring the heat—crafted for real spice lovers.',
        },
        {
          answerText: 'Fruity and Zesty',
          description:
            'From tangy dried fruits to fruit-forward trail mixes, this snack lineup brings a juicy punch to your day — no added sugar required to keep it delicious.',
          id: 'ftb25_v3_q3_fruity-zesty',
          label: 'Fruity & Zesty',
          selector: '[RECOM] Quiz: Fruity and Zesty',
          valueProp:
            'Savor tangy dried fruits and fruit-packed mixes, made with hand-selected, vibrant produce bursting with natural flavor.',
        },
        {
          answerText: 'Chocolaty and Rich',
          description:
            "You're here for the chocolate, and we get it. From silky sweets to rich chocolate-covered snacks, this collection delivers that melt-in-your-mouth magic.",
          id: 'ftb25_v3_q3_chocolaty-rich',
          label: 'Chocolaty & Rich',
          selector: '[RECOM] Quiz: Chocolaty and Rich',
          valueProp:
            "Treat yourself to sweets made with our house-panned chocolate, crafted in small batches for rich, smooth indulgence you won't find anywhere else.",
        },
        {
          answerText: 'Crunchy and Nutty',
          description:
            'Classic, clean, and packed with flavor — these handpicked nuts and nutty brittles bring protein, crunch, and simplicity to every handful.',
          id: 'ftb25_v3_q3_crunchy-nutty',
          label: 'Crunchy & Nutty',
          selector: '[RECOM] Quiz: Crunchy and Nutty',
          valueProp:
            'Dig into crunchy brittles and premium nuts we roast in-house daily—sourced for size, flavor, and freshness you can taste.',
        },
        {
          answerText: 'Sweet and Gummy',
          description:
            "You've got a soft spot for gummies — and we've got the goods. This sweet selection features chewy, fruity snacks that hit all the right nostalgic notes.",
          id: 'ftb25_v3_q3_sweet-gummy',
          label: 'Sweet & Gummy',
          selector: '[RECOM] Quiz: Sweet and Gummy',
          valueProp:
            'Chewy, fruity, and fun—our gummy bears, Swedish Fish, and mix-and-match varieties bring sweetness in every bite.',
        },
      ],
      columns: 2,
      id: 2,
      maxSelections: 3,
      title: 'What types of flavors can we recommend for you?',
    },
    {
      answers: [
        {
          answerText: 'High-quality, premium snacks',
          id: 'ftb25_v3_q4_high-quality-premium-snacks',
        },
        {
          answerText: 'A wide selection of snacks, so you can always try something new',
          id: 'ftb25_v3_q4_wide-selection',
        },
        {
          answerText: 'Convenience - delivered fast and fresh',
          id: 'ftb25_v3_q4_convenience',
        },
        {
          answerText: 'Health, wholesome snacks',
          id: 'ftb25_v3_q4_health-wholesome',
        },
      ],
      columns: 1,
      id: 3,
      title: 'When choosing snacks, what matters the most to you:',
    },
  ];
